import { Component, FormEventHandler, KeyboardEvent, ReactNode } from 'react'
import * as debounce from 'lodash/debounce'
import * as isEqual from 'lodash/isEqual'
import * as styles from 'styles/atoms/inputs/OldSelect.css'
import cn from 'classnames'
import { ErrorItem, InputTheme, Option, QAMarked } from 'interfaces/component'
import TextField from 'components/atoms/inputs/TextField'
import DropdownItems from 'components/atoms/inputs/DropdownItems'
import { DropdownContentRendererOptions } from 'components/atoms/inputs/DropdownItems'
import { doesStringContain, doesStringStartWith } from 'helpers/stringHelpers'
import { WrappedFieldProps } from 'redux-form'
import { State } from 'interfaces/state'
import CrossSvg from 'icons/cross.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { PreventScrollOptional } from 'components/features/pfm/PreventScroll/PreventScrollOptional'

const INPUT_MIN_REQUEST_LENGTH = 1
const INPUT_REQUEST_TIMEOUT = 300
// max count of options that we can receive from backend
const OPTIONS_MAX_LIMIT = 50

type ContentRendererOptions<TValue> = DropdownContentRendererOptions<TValue> & {
  query: string
  noResults: boolean
}

export type ContentRenderer<TValue> = (contentObj: ContentRendererOptions<TValue>) => ReactNode

export type AutosuggestProps<TValue> = QAMarked & WrappedFieldProps<State> & {
  label: string
  placeholder?: string

  options: Option<TValue>[]
  isLoading: boolean
  active: boolean
  disabled?: boolean
  lastSentQuery: string

  selected: null | Option<TValue>
  rounded?: boolean

  errors?: ErrorItem[]
  errored?: boolean
  touched?: boolean
  theme?: InputTheme
  showIcon?: boolean
  showLabelAsLink?: boolean
  requestOptimization?: boolean // Make the text field filter the options
  autoComplete?: string
  autoFocus?: boolean
  onChange: (option: Option<TValue> | null) => void
  onFocus: () => void
  onBlur: () => void
  shouldPlaceSelectedToInput?: boolean
  isAlwaysBelowInput?: boolean

  contentRenderer?: ContentRenderer<TValue>
  onRequestSuggestions: (input: string) => void
}

type AutosuggestState<TValue> = {
  input: string
  shownOptions: Option<TValue>[]
  isDropdownShown: boolean
  hasNoResultsFor: string | null
  that: Autosuggest<TValue>
}

/**
 * When user types autosuggest call suggestions debouncely, case-insensitively.
 * Previous incompleted request should be canceled for correct behavior of autosuggest.
 *
 * Autosuggest remembers last sent query and consider it before sending next query:
 *  if next query starts with previous (for example, 'input' starts with 'inp')
 *  and previous request contains not maximum of suggestions,
 *  autosuggest filters last suggestions.
 */
class Autosuggest<TValue> extends Component<AutosuggestProps<TValue>, AutosuggestState<TValue>> {
  static defaultProps: Partial<AutosuggestProps<void>> = {
    placeholder: '',
    requestOptimization: true,
    theme: 'white',
  }

  static getDerivedStateFromProps (nextProps: AutosuggestProps<any>, prevState: AutosuggestState<any>) {
    const { that } = prevState
    const isValueChanged = nextProps.selected !== that.props.selected
    const isFocused = nextProps.active && !that.props.active
    const isBlurred = !nextProps.active && that.props.active
    const hasSuggestionsUpdated = nextProps.options !== that.props.options
    const isNowDisabled = nextProps.disabled && !that.props.disabled

    const shouldUpdateState = isValueChanged || isBlurred || isFocused || hasSuggestionsUpdated || isNowDisabled
    if (!shouldUpdateState) {
      return null
    }

    const nextLabel = nextProps.shouldPlaceSelectedToInput && nextProps.selected ? nextProps.selected.label : ''
    const input = isValueChanged
      ? nextLabel
      : isBlurred && nextLabel || prevState.input

    const nextShownOptions = that.calculateShownOptions(nextProps.options, input)
    const hasNoResultsFor = hasSuggestionsUpdated ?
      that.getNoResultsFor(nextProps.options, prevState.input, nextProps.lastSentQuery) :
      prevState.hasNoResultsFor

    return {
      input,
      shownOptions: nextShownOptions,
      hasNoResultsFor,
      isDropdownShown: !isNowDisabled &&
        !isValueChanged &&
        input.length >= INPUT_MIN_REQUEST_LENGTH &&
        nextProps.active &&
        (nextShownOptions.length > 0 || Boolean(nextProps.contentRenderer)),
    }
  }

  private dropdown: DropdownItems<TValue> | null

  constructor (props: AutosuggestProps<TValue>, context?: any) {
    super(props, context)

    this.state = {
      input: props.selected ? props.selected.label : '',
      shownOptions: [],
      isDropdownShown: false,
      hasNoResultsFor: null,
      that: this,
    }
  }

  render () {
    const {
      isLoading,
      placeholder,
      label,
      disabled,
      active, onFocus, onBlur,
      errors, errored, touched,
      theme, autoComplete, autoFocus,
      showIcon, showLabelAsLink,
      qaMarker,
      contentRenderer,
      isAlwaysBelowInput,
      rounded,
    } = this.props
    const { input, shownOptions, isDropdownShown, hasNoResultsFor } = this.state

    // TS magic and bugs: remove "any" when TS will manage to generics in jsx
    const Dropdown: new () => DropdownItems<TValue> = DropdownItems as any

    const showErrors = !hasNoResultsFor || hasNoResultsFor && !active

    const clearInputButton = (
      <button className={ styles.clearInput } onClick={ this.handleClearInput }>
        <SvgIcon src={ CrossSvg } />
      </button>
    )

    const rootClassName = cn({
      [styles.roundFrame]: rounded,
    })

    return (
      <div className={ rootClassName }>
        <div className={ styles.autosuggest }>
          <TextField
            active={ active }
            disabled={ disabled }
            label={ label }
            value={ input }
            placeholder={ placeholder }
            ellipsis={ isDropdownShown ? 'none' : 'none-focus' }
            ellipsisTheme={ theme }
            autoComplete={ autoComplete }
            autoFocus={ autoFocus }
            showLabelAsLink={ showLabelAsLink }
            isLoading={ isLoading }
            errors={ showErrors ? errors : [] }
            errored={ errored }
            touched={ touched }
            onChange={ this.handleChange }
            onFocus={ onFocus }
            onBlur={ onBlur }
            onKeyDown={ this.handleKeyDown }
            qaMarker={ qaMarker }
            qaType='autosuggest'
          />
          { showIcon && (!!input.length ? clearInputButton : <div className={ styles.searchTextboxIcon } />) }
          <PreventScrollOptional
            allowScroll
            isWrapped={ shownOptions.length !== 0 }
          >
            <Dropdown
              ref={ (el) => this.dropdown = el }
              isShown={ isDropdownShown }
              options={ shownOptions }
              selected={ null }
              onSelect={ this.handleSelect }
              contentRenderer={ contentRenderer ? this.renderContent : undefined }
              darkTheme={ theme === 'dark' }
              isAlwaysBelowInput={ isAlwaysBelowInput }
              qaMarker={ qaMarker }
              qaType='autosuggest'
            />
          </PreventScrollOptional>
          { this.renderNoResults() }
        </div>
      </div>
    )
  }

  renderContent = (options: DropdownContentRendererOptions<TValue>) => {
    const { contentRenderer } = this.props
    const { input, shownOptions } = this.state

    if (!contentRenderer) {
      return
    }

    return contentRenderer({
      ...options,
      query: input,
      noResults: !shownOptions.length,
    })
  }

  renderNoResults () {
    const {
      isLoading,
      active,
      contentRenderer,
    } = this.props
    const { hasNoResultsFor } = this.state

    if (contentRenderer || !active || isLoading || !hasNoResultsFor) {
      return null
    }

    return (
      <div className={ styles.noResults }>
        { `No results match "${ hasNoResultsFor }"` }
      </div>
    )
  }

  componentWillUnmount () {
    this.requestSuggestions.cancel()
  }

  private calculateShownOptions = (options: Option<TValue>[], input: string, forceFilter?: boolean) => {
    const { requestOptimization } = this.props

    // avoid extra request
    if (requestOptimization) {
      return options.filter((option) => doesStringStartWith(option.label, input))
    }

    // if `requestOptimization` is false, we still need to filter options in `onChange` handler
    if (forceFilter) {
      return options.filter((option) => doesStringContain(option.label, input))
    }

    return options
  }

  private requestSuggestions = debounce((input: string) => {
    const { options, requestOptimization, lastSentQuery } = this.props
    const filteredOptions = this.calculateShownOptions(options, input)
    const shouldRequestOverLimit = options.length >= OPTIONS_MAX_LIMIT &&
      filteredOptions.length < options.length

    const isInputSupersetOfLast = doesStringStartWith(input, lastSentQuery)

    const shouldMakeRequest = !lastSentQuery ||
      shouldRequestOverLimit ||
      !isInputSupersetOfLast ||
      !requestOptimization

    if (shouldMakeRequest) {
      this.props.onRequestSuggestions(input)
    }
  }, INPUT_REQUEST_TIMEOUT)

  private getNoResultsFor (options: Option<TValue>[], input: string, lastSentQuery: string): string | null {
    if (input.length === 0 || !lastSentQuery || !doesStringStartWith(input, lastSentQuery)) {
      return null
    }

    if (options.length === 0) {
      return lastSentQuery
    }

    const hasNotShownOptions = this.calculateShownOptions(options, input).length === 0
    if (hasNotShownOptions && options.length < OPTIONS_MAX_LIMIT) {
      return input
    }

    return null
  }

  private handleChange: FormEventHandler<HTMLInputElement> = (e) => {
    const {
      active,
      options,
      lastSentQuery,
      contentRenderer,
    } = this.props

    const { value: input } = e.currentTarget
    const nextShownOptions = this.calculateShownOptions(options, input, true)
    const hasNoResultsFor = this.getNoResultsFor(options, input, lastSentQuery)

    this.setState({
      input,
      shownOptions: nextShownOptions,
      isDropdownShown:
        active &&
        input.length >= INPUT_MIN_REQUEST_LENGTH &&
        (nextShownOptions.length > 0 || Boolean(contentRenderer)),
      hasNoResultsFor: hasNoResultsFor,
    })

    if (input.length >= INPUT_MIN_REQUEST_LENGTH) {
      this.requestSuggestions(input)
    }
  }

  private handleClearInput = () => {
    this.setState({ input: '' })
    this.props.onChange(null)
  }

  private handleSelect = (nextSelected: Option<TValue>) => {
    const { onChange, selected, options, shouldPlaceSelectedToInput } = this.props

    // if it's already selected then do not emit action, just update state
    if (shouldPlaceSelectedToInput && isEqual(selected, nextSelected)) {
      this.setState({
        input: nextSelected.label,
        shownOptions: this.calculateShownOptions(options, nextSelected.label),
        isDropdownShown: false,
      })
    } else {
      onChange(nextSelected)
    }
  }

  private handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { isDropdownShown, shownOptions } = this.state
    const { dropdown } = this

    if (shownOptions.length === 0 || !dropdown) {
      return
    }

    switch (event.key) {
      case 'Enter': {
        if (isDropdownShown) {
          if (shownOptions.length === 1) {
            this.handleSelect(shownOptions[0])
          } else {
            dropdown.handleEnter()
          }
        } else {
          this.setState({ isDropdownShown: true })
        }
        event.preventDefault()
        return
      }

      case 'Escape':
        if (isDropdownShown) {
          this.setState({ isDropdownShown: false })
        }
        event.preventDefault()
        return

      case 'ArrowUp':
        if (!isDropdownShown) {
          this.setState({ isDropdownShown: true })
        }
        dropdown.handleArrow('up')
        event.preventDefault()
        return

      case 'ArrowDown':
        if (!isDropdownShown) {
          this.setState({ isDropdownShown: true })
        }
        dropdown.handleArrow('down')
        event.preventDefault()
        return
    }
  }
}

export default Autosuggest
